import React, { useCallback, useEffect, useRef, useState } from "react";
import OrderChatPanel from "./OrderChatPanel";
import {
  useGetRoomDataByOrderAndVideoIdQuery,
  useGetMessagesQuery,
  useSendMessageForOrderModuleMutation,
  useGetUserOnlineOfflineStatusQuery,
  useMarkRoomReadMutation,
} from "@/redux/feature/chat/chatApi";
import { useChatSocket } from "@/hooks/useChatSocket";
import {
  joinChatRoom,
  leaveChatRoom,
  sendTypingStatus,
  getChatSocket,
  subscribeChatSocketConnect,
} from "@/services/chatSocketService";
import type { TChatRoom } from "@/redux/feature/chat/chatType";
import { useGlobalContext } from "@/context/GlobalContext";
import { TChatMessage } from "@/pages/messaging/ChatWindow";
import toast from "react-hot-toast";

export type ClientInfo = {
  clientUserId: string;
  clientName: string;
  avatarUrl?: string | null;
  statusDot?: "online" | "offline" | "away";
  meta?: string;
};

export type CreatorOrderChatSidebarProps = {
  orderId: string;
  videoId: string;
  client: ClientInfo | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  unreadCount?: number;
};

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function isHistoryForCurrentThread(
  room: TChatRoom,
  activeRoomId: string,
  activeVideoId: string,
  historyMessages: unknown[] | undefined,
): boolean {
  if (String(room.room_id) !== String(activeRoomId)) return false;
  if (room.video_id != null && String(room.video_id) !== "") {
    if (String(room.video_id) !== String(activeVideoId)) return false;
  }
  const first = historyMessages?.[0] as
    | { chat_room_id?: string | { _id?: string } }
    | undefined;
  const cr = first?.chat_room_id;
  if (cr != null && room._id) {
    const crId =
      typeof cr === "object" && cr && "_id" in cr
        ? String((cr as { _id?: string })._id)
        : String(cr);
    if (crId && crId !== String(room._id)) return false;
  }
  return true;
}

function incomingMessageRoomId(m: any): string {
  if (m?.room_id != null && m.room_id !== "") return String(m.room_id);
  if (m?.chat_room_id?.room_id != null) return String(m.chat_room_id.room_id);
  const cr = m?.chat_room_id;
  if (cr != null && typeof cr === "object" && "_id" in cr)
    return String((cr as { _id: unknown })._id);
  if (typeof cr === "string" || typeof cr === "number") return String(cr);
  return "";
}

function ChatLauncher({
  client,
  unreadCount = 0,
  onClick,
}: {
  client: ClientInfo;
  unreadCount?: number;
  onClick: () => void;
}) {
  const clientFirstName = client?.clientName?.trim().split(/\s+/)[0];
  const initials = client?.clientName
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open chat"
      className={cn(
        "fixed bottom-4 right-4 z-[70] md:hidden",
        "flex items-center gap-2.5 rounded-full bg-white",
        "px-2 py-1",
        "border border-[#E9EAEB]",
        "shadow-[0_8px_24px_rgba(16,24,40,0.14)]",
        "max-w-[calc(100vw-32px)]",
      )}
    >
      <div className="relative shrink-0">
        <div className="flex size-[28px] items-center justify-center overflow-hidden rounded-full border border-[#EAECF0] bg-white">
          {client?.avatarUrl ? (
            <img
              src={client.avatarUrl}
              alt={clientFirstName}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-xs font-semibold text-[#344054]">
              {initials || "C"}
            </span>
          )}
        </div>

        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#FF6A00] px-1 text-[12px] font-semibold leading-none text-white shadow-[0_2px_8px_rgba(255,106,0,0.35)]">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </div>

      <span className="truncate text-[14px] font-medium leading-none text-[#475467]">
        Chat
      </span>
    </button>
  );
}

function MobileChatModal({
  open,
  onClose,
  client,
  children,
}: {
  open: boolean;
  onClose: () => void;
  client: ClientInfo | null;
  children: React.ReactNode;
}) {
  const clientFirstName = client?.clientName?.trim().split(/\s+/)[0];

  return (
    <div
      className={cn(
        "fixed inset-0 z-[80] transition-all duration-300 md:hidden",
        open ? "pointer-events-auto" : "pointer-events-none",
      )}
    >
      <div
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-black/20 transition-opacity duration-300",
          open ? "opacity-100" : "opacity-0",
        )}
      />

      <div
        className={cn(
          "absolute inset-0 overflow-hidden bg-[#F3F4F6]",
          "transition-all duration-300",
          open ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 border-b border-[#E4E7EC] bg-[#F3F4F6] px-4 py-4 pt-[max(16px,env(safe-area-inset-top))]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-[18px] font-semibold leading-7 text-[#111827]">
                  {clientFirstName || "Client"}
                </div>
                <div className="mt-0.5 text-[13px] leading-4 text-[#111827]">
                  Project chat
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close chat"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E5E7EB] text-[#111827]"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6L18 18M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function CreatorOrderChatSidebar({
  orderId,
  videoId,
  client,
  open,
  onOpenChange,
  unreadCount = 0,
}: CreatorOrderChatSidebarProps) {
  const {
    data: roomRes,
    isFetching: roomLoading,
    refetch: refetchRoom,
  } = useGetRoomDataByOrderAndVideoIdQuery(
    { order_id: orderId, video_id: videoId },
    {
      skip: !orderId || !videoId,
      refetchOnMountOrArgChange: true,
    },
  );

  const room = roomRes?.data;
  const roomId = room?.room_id;
  const chatClosed =
    room?.status === "closed" || room?.isReadOnly === true;

  const { socket, connected, userId } = useChatSocket(true);
  const { userData } = useGlobalContext();
  const myRole = "creator" as const;

  const clientId = room?.client_id?._id;
  const creatorId = room?.creator_id?._id;

  const otherUserId =
    userId && clientId && creatorId
      ? userId === clientId
        ? creatorId
        : clientId
      : client?.clientUserId;

  const { data: onlineStatusDTO } = useGetUserOnlineOfflineStatusQuery(
    otherUserId as string,
    { skip: !otherUserId },
  );

  const onLineStatus = onlineStatusDTO?.data?.online;

  const { data: historyRes, isFetching: messagesLoading } = useGetMessagesQuery(
    roomId ? { roomId, limit: 50, page: 1 } : (undefined as any),
    { skip: !open || !roomId },
  );

  useEffect(() => {
    if (client?.clientUserId && open && !roomId) {
      refetchRoom();
    }
  }, [client?.clientUserId, open, roomId, refetchRoom]);

  useEffect(() => {
    if (!open || !connected || !roomId) return;
    joinChatRoom(roomId);
    return () => {
      leaveChatRoom(roomId);
    };
  }, [open, connected, roomId]);

  useEffect(() => {
    if (!open || !roomId) return;
    const rejoin = () => {
      if (!open || !roomId) return;
      joinChatRoom(roomId);
    };
    const unsub = subscribeChatSocketConnect(rejoin);
    const s = getChatSocket();
    if (s?.connected) rejoin();
    return unsub;
  }, [open, roomId]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const [messages, setMessages] = useState<TChatMessage[]>([]);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const optimisticMessagesRef = useRef<Map<string, TChatMessage>>(new Map());
  const lastMarkedReadMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    seenIdsRef.current = new Set();
    optimisticMessagesRef.current = new Map();
    lastMarkedReadMessageIdRef.current = null;
    setMessages([]);
  }, [roomId, videoId]);

  const resolveSenderRole = useCallback(
    (senderIdRaw: any): "client" | "creator" => {
      const senderId = (senderIdRaw?._id ?? senderIdRaw)?.toString?.() ?? "";
      return senderId && userId && senderId === String(userId)
        ? myRole
        : "client";
    },
    [userId],
  );

  useEffect(() => {
    const list = historyRes?.data?.messages;
    if (!list || !room || !roomId) return;
    if (
      !isHistoryForCurrentThread(room, roomId, videoId, list as unknown[])
    ) {
      return;
    }

    const mapped: TChatMessage[] = list.map((m: any) => {
      const sender = resolveSenderRole(m.sender_id);
      const senderName = m?.sender_id?.fullName ?? "";

      return {
        id: m._id,
        sender,
        senderName,
        content: m.content ?? "",
        timestamp: new Date(m.timestamp),
      };
    });

    seenIdsRef.current = new Set(mapped.map((x) => x.id));
    setMessages((prev) => {
      if (
        prev.length === mapped.length &&
        prev.every((m, i) => m.id === mapped[i]?.id)
      ) {
        return prev;
      }
      return mapped;
    });
  }, [historyRes, room, roomId, videoId, resolveSenderRole]);

  const handleReceiveMessage = useCallback(
    (m: any) => {
      const incomingRoomId = incomingMessageRoomId(m);
      if (incomingRoomId && roomId && incomingRoomId !== String(roomId)) {
        return;
      }

      const id = m?._id ?? `sock_${Date.now()}`;
      if (seenIdsRef.current.has(id)) return;

      const sender = resolveSenderRole(m.sender_id);
      const senderName = m?.sender_id?.fullName ?? "";
      const senderId = (m?.sender_id?._id ?? m?.sender_id)?.toString?.() ?? "";
      const isFromMe = senderId && userId && senderId === String(userId);
      const content = m.content ?? "";

      if (isFromMe && optimisticMessagesRef.current.size > 0) {
        let matchedOptimisticId: string | null = null;
        for (const [
          tempId,
          optimisticMsg,
        ] of optimisticMessagesRef.current.entries()) {
          if (optimisticMsg.content === content) {
            matchedOptimisticId = tempId;
            break;
          }
        }
        if (matchedOptimisticId) {
          seenIdsRef.current.add(id);
          optimisticMessagesRef.current.delete(matchedOptimisticId);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === matchedOptimisticId
                ? {
                    ...msg,
                    id,
                    senderName,
                    timestamp: new Date(m.timestamp),
                  }
                : msg,
            ),
          );
          return;
        }
      }

      const msg: TChatMessage = {
        id,
        sender,
        senderName,
        content,
        timestamp: new Date(m.timestamp),
      };

      seenIdsRef.current.add(id);
      setMessages((prev) => [...prev, msg]);
    },
    [roomId, userId, resolveSenderRole],
  );

  const handleMessageBlockedWarning = useCallback(
    (payload: any) => {
      if (String(payload?.roomId || "") !== String(roomId || "")) return;
      const warningId = `blocked_${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: warningId,
          sender: myRole,
          senderName: userData?.fullName || "You",
          content: payload?.content || "",
          timestamp: new Date(payload?.blockedAt || Date.now()),
          isBlocked: true,
          warningText:
            payload?.warningText ||
            "This kind of message can lead to a permanent ban from the platform.",
        },
      ]);
    },
    [roomId, userData?.fullName],
  );

  useEffect(() => {
    if (!socket || !open || !roomId) return;

    socket.off("receiveMessage", handleReceiveMessage);
    socket.off("messageBlockedWarning", handleMessageBlockedWarning);
    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("messageBlockedWarning", handleMessageBlockedWarning);

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("messageBlockedWarning", handleMessageBlockedWarning);
    };
  }, [socket, open, roomId, handleReceiveMessage, handleMessageBlockedWarning]);

  const [sendMessageForOrder, { isLoading: sending }] =
    useSendMessageForOrderModuleMutation();
  const [markRoomRead] = useMarkRoomReadMutation();

  useEffect(() => {
    if (!socket || !connected || !open || !roomId) return;
    if (!messages.length) return;

    const last = messages[messages.length - 1];
    if (last.sender === myRole) return;
    if (lastMarkedReadMessageIdRef.current === last.id) return;

    lastMarkedReadMessageIdRef.current = last.id;
    socket.emit("roomRead", { roomId });
    markRoomRead(roomId).catch(() => {});
  }, [roomId, socket, connected, open, messages, markRoomRead]);

  const handleSend = async (text: string) => {
    const content = text.trim();
    if (!content) return;

    const receiverId = client?.clientUserId || room?.client_id?._id;
    if (!receiverId) return;

    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const optimisticMessage: TChatMessage = {
      id: tempId,
      sender: myRole,
      senderName: userData?.fullName || "You",
      content,
      timestamp: new Date(),
    };
    optimisticMessagesRef.current.set(tempId, optimisticMessage);
    setMessages((prev) => [...prev, optimisticMessage]);

    if (connected && roomId) {
      sendTypingStatus(roomId, false);
    }

    try {
      await sendMessageForOrder({
        receiver_id: String(receiverId),
        content,
        message_type: "text",
        chat_type: "order_video",
        order_id: orderId,
        video_id: videoId,
      }).unwrap();
    } catch (e) {
      const blocked = (e as any)?.data?.code === "CONTACT_MESSAGE_BLOCKED";
      if (blocked) {
        optimisticMessagesRef.current.delete(tempId);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? {
                  ...msg,
                  isBlocked: true,
                  warningText:
                    "This kind of message can lead to a permanent ban from the platform.",
                }
              : msg,
          ),
        );
        return;
      }

      optimisticMessagesRef.current.delete(tempId);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      toast.error((e as any)?.data?.message || "Failed to send message");
    }
  };

  const clientFirstName = client?.clientName?.trim().split(/\s+/)[0];
  const loading = roomLoading || messagesLoading;
  const composerPlaceholder =
    messages.length === 0 ? "Type your message here" : "Reply ...";

  const reconnectBanner =
    open && roomId && client && !connected ? (
      <div
        className="mb-2 shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-center text-[11px] font-medium leading-4 text-amber-950"
        role="status"
      >
        Reconnecting to live chat…
      </div>
    ) : null;

  const renderNoClient = (
    <div className="grid h-full place-items-center px-6">
      <div className="text-center">
        <div className="text-sm font-semibold text-[#101828]">
          Chat unavailable
        </div>
        <div className="mt-1 text-xs text-[#667085]">
          Client details are not available for this project yet.
        </div>
      </div>
    </div>
  );

  const desktopChat = !client ? (
    renderNoClient
  ) : (
    <OrderChatPanel
      room={room ?? null}
      myUserId={userId || ""}
      myRole={myRole}
      messages={messages}
      loading={loading}
      onSend={handleSend}
      placeholder={composerPlaceholder}
      disabled={sending || !client?.clientUserId || chatClosed}
      closedNotice={
        chatClosed
          ? "This chat is closed. The project has moved to the next stage."
          : undefined
      }
      collapsed={false}
      onCollapse={() => onOpenChange(false)}
      statusDot={onLineStatus ? "online" : "offline"}
      showHeader={true}
      showSecurityReminder={true}
      avatarUrl={client.avatarUrl || undefined}
      headerNameOverride={clientFirstName}
    />
  );

  const mobileChat = !client ? (
    renderNoClient
  ) : (
    <OrderChatPanel
      room={room ?? null}
      myUserId={userId || ""}
      myRole={myRole}
      messages={messages}
      loading={loading}
      onSend={handleSend}
      placeholder={composerPlaceholder}
      disabled={sending || !client?.clientUserId || chatClosed}
      closedNotice={
        chatClosed
          ? "This chat is closed. The project has moved to the next stage."
          : undefined
      }
      collapsed={false}
      onCollapse={() => onOpenChange(false)}
      statusDot={onLineStatus ? "online" : "offline"}
      showHeader={false}
      showSecurityReminder={true}
      borderless={true}
      bodyClassName="bg-[#F3F4F6]"
      avatarUrl={client.avatarUrl || undefined}
      headerNameOverride={clientFirstName}
    />
  );

  return (
    <>
      <div
        className={cn(
          "hidden md:flex md:shrink-0 md:self-start",
          open ? "md:w-[380px] xl:w-[420px]" : "md:w-0",
        )}
      >
        <div
          className={cn(
            "w-full border-l border-[#DFDFDF] bg-white transition-[opacity] duration-300 ease-out",
            "md:sticky md:top-6",
            "md:h-[calc(100vh-240px)] md:max-h-[560px]",
            open
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0",
          )}
        >
          <div className="flex h-full min-h-0 w-full flex-col p-3">
            {reconnectBanner}
            <div className="flex min-h-0 flex-1 flex-col">{desktopChat}</div>
          </div>
        </div>
      </div>

      {client && !open ? (
        <ChatLauncher
          client={client}
          unreadCount={unreadCount}
          onClick={() => onOpenChange(true)}
        />
      ) : null}

      <MobileChatModal
        open={open}
        onClose={() => onOpenChange(false)}
        client={client}
      >
        <div className="flex h-full min-h-0 flex-col">
          {reconnectBanner}
          <div className="min-h-0 flex-1">{mobileChat}</div>
        </div>
      </MobileChatModal>
    </>
  );
}
